import React, { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Loader2 } from 'lucide-react';
import productionService from '../../services/productionService';

function ProductionStepTracker({
  steps,
  orderId,
  itemIndex,
  plannedQuantity,
  onProgressUpdate,
  readOnly = false,
}) {
  const [loading, setLoading] = useState({});
  const [quantityInputs, setQuantityInputs] = useState({});
  const [scrapInputs, setScrapInputs] = useState({});

  const activeStepIndex = useMemo(() => {
    return steps.findIndex(step => step.quantity_pending > 0);
  }, [steps]);

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'warning';
      case 'pending':
      default:
        return 'secondary';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return '已完成';
      case 'in_progress':
        return '进行中';
      case 'pending':
      default:
        return '待开始';
    }
  };

  const getRowClassName = (stepIndex, step) => {
    if (step.status === 'completed') {
      return 'bg-green-50';
    }
    if (stepIndex === activeStepIndex) {
      return 'bg-blue-50';
    }
    return 'bg-gray-50';
  };

  const canUpdateStep = (stepIndex) => {
    if (readOnly) return false;
    if (stepIndex === 0) {
      return steps[0].quantity_pending > 0;
    }
    const previousStep = steps[stepIndex - 1];
    const currentStep = steps[stepIndex];
    return previousStep?.status === 'completed' && currentStep.quantity_pending > 0;
  };

  const handleQuantityChange = (stepSequence, value) => {
    setQuantityInputs(prev => ({
      ...prev,
      [stepSequence]: value
    }));
  };

  const handleScrapChange = (stepSequence, value) => {
    setScrapInputs(prev => ({
      ...prev,
      [stepSequence]: value
    }));
  };

  const handleComplete = async (step, stepIndex, recordScrap = false) => {
    const sequence = step.sequence;

    setLoading(prev => ({ ...prev, [sequence]: true }));

    try {
      let quantity = 0;
      let scrapQty = 0;

      if (recordScrap) {
        // Scrap mode: only record scrap quantity
        scrapQty = parseFloat(scrapInputs[sequence]) || 0;
        if (scrapQty <= 0) {
          setLoading(prev => ({ ...prev, [sequence]: false }));
          return;
        }
      } else {
        // Complete mode: use completion quantity
        const inputQty = parseFloat(quantityInputs[sequence]) || step.quantity_pending;
        quantity = Math.min(inputQty, step.quantity_pending);
        if (quantity <= 0) {
          setLoading(prev => ({ ...prev, [sequence]: false }));
          return;
        }
      }

      const result = await productionService.updateStepProgress(
        orderId,
        itemIndex,
        sequence,
        quantity,
        scrapQty
      );

      // Clear inputs after successful update
      setQuantityInputs(prev => ({ ...prev, [sequence]: '' }));
      setScrapInputs(prev => ({ ...prev, [sequence]: '' }));

      if (onProgressUpdate) {
        onProgressUpdate(result);
      }
    } catch (error) {
      console.error('Failed to update step progress:', error);
    } finally {
      setLoading(prev => ({ ...prev, [sequence]: false }));
    }
  };

  const calculateProgress = (step) => {
    if (!plannedQuantity || plannedQuantity === 0) return 0;
    return Math.round((step.quantity_completed / plannedQuantity) * 100);
  };

  if (!steps || steps.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        暂无工序步骤
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">工序进度跟踪</h3>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16 text-center">序号</TableHead>
              <TableHead>工序名称</TableHead>
              <TableHead className="text-center">标准工时</TableHead>
              <TableHead className="text-center">完成/计划</TableHead>
              <TableHead className="w-32">进度</TableHead>
              <TableHead className="text-center">状态</TableHead>
              {!readOnly && <TableHead className="text-center">操作</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {steps.map((step, index) => {
              const progress = calculateProgress(step);
              const isLoading = loading[step.sequence];
              const canUpdate = canUpdateStep(index);

              return (
                <TableRow key={step.sequence} className={getRowClassName(index, step)}>
                  <TableCell className="text-center font-medium">
                    {step.sequence}
                  </TableCell>
                  <TableCell className="font-medium">{step.name}</TableCell>
                  <TableCell className="text-center">{step.standard_time}</TableCell>
                  <TableCell className="text-center">
                    {step.quantity_completed} / {plannedQuantity}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <Progress value={progress} className="h-2" />
                      <span className="text-xs text-muted-foreground">{progress}%</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={getStatusBadgeVariant(step.status)}>
                      {getStatusLabel(step.status)}
                    </Badge>
                  </TableCell>
                  {!readOnly && (
                    <TableCell className="text-center">
                      {step.status !== 'completed' && (
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <Input
                            type="number"
                            min="0"
                            max={step.quantity_pending}
                            value={quantityInputs[step.sequence] || ''}
                            onChange={(e) => handleQuantityChange(step.sequence, e.target.value)}
                            placeholder='数量'
                            className="w-20 h-8 text-center"
                            disabled={!canUpdate || isLoading}
                          />
                          <Input
                            type="number"
                            min="0"
                            value={scrapInputs[step.sequence] || ''}
                            onChange={(e) => handleScrapChange(step.sequence, e.target.value)}
                            placeholder="废0"
                            className="w-16 h-8 text-center"
                            disabled={!canUpdate || isLoading}
                          />
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => handleComplete(step, index, false)}
                            disabled={!canUpdate || isLoading}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {isLoading ? (
                              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            ) : null}
                            完成
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleComplete(step, index, true)}
                            disabled={!canUpdate || isLoading}
                          >
                            报废
                          </Button>
                        </div>
                      )}
                      {step.status === 'completed' && (
                        <span className="text-sm text-muted-foreground">已完成</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ProductionStepTracker;
